/**
 * AuditorSearch — la caja de búsqueda y los cuatro candidatos.
 *
 * **Mínimo 3 letras y 400 ms de espera.** Cada búsqueda es una llamada de pago
 * a Google. Sin el retardo, escribir «cafetería la plaza» serían dieciocho
 * llamadas y el throttle de 20/min saltaría antes de terminar de teclear el
 * nombre.
 *
 * **UN SOLO CAMPO.** Hubo un segundo para la ciudad, opcional, que el backend
 * concatenaba al texto. Se quitó: dos cajas en una landing parecen un
 * formulario y frenan a quien solo quería escribir el nombre de su bar, y el
 * `near` era opcional de todas formas —casi nadie lo rellenaba—. La ciudad
 * sigue desempatando entre los cuarenta «El Rincón» de España, solo que ahora
 * se escribe en la misma caja y viaja dentro de `query`: la búsqueda de texto
 * de Google entiende «El Rincón Málaga» igual de bien.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 400;

export default function AuditorSearch({
  c,
  results,
  searching,
  onSearch,
  onPick,
}) {
  const s = c.search;
  const [query, setQuery] = useState('');
  const timer = useRef(null);
  // Lo último que se pidió, para no repetir la misma llamada cuando el usuario
  // solo mueve el cursor o añade y quita una letra.
  const lastSent = useRef('');

  useEffect(() => {
    const trimmed = query.trim();
    clearTimeout(timer.current);

    if (trimmed.length < MIN_CHARS) {
      lastSent.current = '';
      return undefined;
    }

    if (trimmed === lastSent.current) return undefined;

    timer.current = setTimeout(() => {
      lastSent.current = trimmed;
      onSearch({ query: trimmed });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer.current);
    // `onSearch` viene memoizado del orquestador; si dejara de estarlo, este
    // efecto se re-armaría en cada render y el debounce no serviría de nada.
  }, [query, onSearch]);

  const tooShort = query.trim().length > 0 && query.trim().length < MIN_CHARS;

  return (
    <div>
      <div>
        <label className="relative block">
          <span className="sr-only">{s.label}</span>
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={s.placeholder}
            className="w-full h-14 pl-11 pr-4 rounded-full border border-line bg-white text-ink placeholder:text-slate-400 focus:outline-none focus:border-[#feae2c] transition-colors"
          />
        </label>
      </div>

      {tooShort && (
        <p className="mt-3 px-2 text-sm text-slate-500">{s.minChars}</p>
      )}

      {searching && (
        <p className="mt-3 px-2 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {s.searching}
        </p>
      )}

      {!searching && results !== null && results.length === 0 && !tooShort && (
        <p className="mt-3 px-2 text-sm text-slate-500">{s.noResults}</p>
      )}

      {!searching && results !== null && results.length > 0 && (
        <div className="mt-4">
          <p className="px-2 mb-2 text-sm font-semibold text-slate-500">{s.pickTitle}</p>
          <ul className="grid gap-2">
            {results.map((result) => (
              <li key={result.placeId}>
                <button
                  type="button"
                  onClick={() => onPick(result)}
                  className="w-full text-left px-4 py-3 rounded-2xl border border-line bg-white hover:border-[#feae2c] transition-colors"
                >
                  <span className="block font-semibold text-ink">{result.mainText}</span>
                  {result.secondaryText && (
                    <span className="block text-sm text-slate-500">{result.secondaryText}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
